import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useFlags } from "@/lib/flags";
import SEO from "@/components/site/SEO";
import { dedupeHeadNow } from "@/components/site/HeadDedupe";
import { getSchoolUrl } from "@/lib/slug";
import { StarRating } from "@/components/site/Stars";
import { formatSppRange } from "@/lib/fees";
import { Building2, MapPin, Users, ArrowRight, GraduationCap } from "lucide-react";

const fmtIDR = (n) => (typeof n === "number" && n > 0 ? `Rp ${Math.round(n / 1_000_000)} jt` : "—");

/** /school-groups/:slug — Foundation page with hierarchical Group → Campus → Unit layout.
 *  Each Campus is rendered as a card, and the school units (TK/SD/SMP/SMA) that live under
 *  the campus are listed inside the same card so parents can quickly scan the whole family. */
export default function SchoolGroupDetail() {
  const { school_reviews_enabled: reviewsOn } = useFlags();
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/school-groups/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); });
  }, [slug]);

  // Page-level SEO owns group metadata on this route — run the synchronous
  // head safety net at the exact commit where group data (and therefore the
  // Helmet tags) becomes available. No timers, no observers.
  useEffect(() => {
    if (data) dedupeHeadNow();
  }, [data]);

  if (notFound) return (
    <div className="max-w-2xl mx-auto py-20 px-4 text-center">
      <div className="font-serif-display text-3xl text-[#1F2937]">Grup tidak ditemukan</div>
      <button onClick={() => nav("/sekolah")} className="mt-4 text-[#D2673E] underline-offset-4 hover:underline">Kembali ke daftar sekolah →</button>
    </div>
  );
  if (!data) return <div className="p-10 text-center text-[#4B5563]">Memuat…</div>;

  const { group, campuses } = data;

  return (
    <div data-testid="group-detail-page" className="max-w-full sm:max-w-7xl sm:mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12 pb-32">
      <SEO title={`${group.name} — The School Reports`} description={group.description || `Yayasan pendidikan ${group.name} dengan ${group.campus_count || 0} cabang di Indonesia.`} />

      {/* Hero */}
      <div className="rounded-2xl bg-white border border-[#EAE7E0] p-6 sm:p-10 shadow-[0_4px_18px_-4px_rgba(31,41,55,0.08)]">
        <div className="flex items-start gap-6 flex-wrap">
          {group.logo_url ? (
            <img src={group.logo_url} alt={group.name} className="w-24 h-24 rounded-2xl object-contain bg-[#F3F1ED] p-2" data-testid="group-logo" />
          ) : (
            <div className="w-24 h-24 rounded-2xl bg-[#FDF2EF] text-[#D2673E] flex items-center justify-center"><Users size={32} /></div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#D2673E] font-semibold">Yayasan Pendidikan</div>
            <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1F2937] mt-1 leading-tight" data-testid="group-name">{group.name}</h1>
            {group.description && <p className="mt-3 text-[15px] text-[#374151] leading-relaxed max-w-[65ch]" data-testid="group-description">{group.description}</p>}
            <div className="mt-4 flex flex-wrap gap-6 text-sm">
              <div><span className="text-[#4B5563]">Cabang:</span> <strong className="text-[#1F2937]" data-testid="group-campus-count">{group.campus_count || 0}</strong></div>
              <div><span className="text-[#4B5563]">Total unit:</span> <strong className="text-[#1F2937]">{group.unit_count || 0}</strong></div>
              {reviewsOn && <div><span className="text-[#4B5563]">Total review:</span> <strong className="text-[#1F2937]">{group.review_count || 0}</strong></div>}
              <div><span className="text-[#4B5563]">Rentang SPP:</span> <strong className="text-[#1F2937]">{fmtIDR(group.spp_min)}–{fmtIDR(group.spp_max)}</strong></div>
            </div>
            {group.website && <a href={group.website} target="_blank" rel="noreferrer" data-testid="group-website" className="mt-4 inline-flex items-center gap-1 text-sm text-[#D2673E] hover:underline">Kunjungi situs resmi →</a>}
          </div>
        </div>
      </div>

      {/* Campus cards (each with nested units) */}
      <div className="mt-10">
        <h2 className="font-serif-display text-2xl text-[#1F2937] mb-4">Cabang &amp; Unit Sekolah</h2>
        {campuses.length === 0 ? (
          <div className="text-[#4B5563] italic">Belum ada cabang terdaftar untuk grup ini.</div>
        ) : (
          <div className="space-y-4" data-testid="campus-list">
            {campuses.map((c) => <CampusCard key={c.campus_id} campus={c} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function CampusCard({ campus }) {
  const { school_reviews_enabled: reviewsOn } = useFlags();
  const units = campus.units || [];
  return (
    <div className="bg-white border border-[#EAE7E0] rounded-2xl p-5 sm:p-6 hover:border-[#D2673E]/60 transition-colors" data-testid={`campus-card-${campus.campus_id}`}>
      {/* Campus header */}
      <div className="flex items-start gap-4">
        {campus.logo_url ? (
          <img src={campus.logo_url} alt={campus.name} className="w-14 h-14 rounded-xl object-contain bg-[#F3F1ED] p-1.5 shrink-0" />
        ) : (
          <div className="w-14 h-14 rounded-xl bg-[#EFF6FF] text-[#1E3A8A] flex items-center justify-center shrink-0"><Building2 size={22} /></div>
        )}
        <div className="flex-1 min-w-0">
          <Link to={`/cabang/${campus.slug}`} data-testid={`campus-link-${campus.campus_id}`}
            className="font-serif-display text-xl text-[#1F2937] hover:text-[#D2673E] transition-colors line-clamp-2">
            {campus.name}
          </Link>
          <div className="mt-1 text-xs text-[#4B5563] flex items-center gap-1"><MapPin size={11} /> {campus.city || "—"}</div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#4B5563]">
            <span>{units.length} unit</span>
            <span>·</span>
            {reviewsOn && <><span>{campus.review_count || 0} review</span>
            <span>·</span></>}
            <span>SPP {fmtIDR(campus.spp_min)}–{fmtIDR(campus.spp_max)}</span>
          </div>
        </div>
        <Link to={`/cabang/${campus.slug}`}
          className="hidden sm:inline-flex items-center gap-1 text-xs text-[#D2673E] font-semibold whitespace-nowrap hover:underline"
          data-testid={`campus-view-all-${campus.campus_id}`}>
          Lihat cabang <ArrowRight size={11} />
        </Link>
      </div>

      {/* Nested units list */}
      {units.length > 0 ? (
        <div className="mt-4 pt-4 border-t border-[#EAE7E0]">
          <div className="text-[10px] uppercase tracking-widest text-[#4B5563] font-semibold mb-2">Unit di cabang ini</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`unit-list-${campus.campus_id}`}>
            {units.map((u) => (
              <Link key={u.school_id} to={getSchoolUrl(u)} data-testid={`unit-item-${u.school_id}`}
                className="group flex items-center gap-3 p-3 rounded-xl border border-[#EAE7E0] hover:border-[#D2673E] hover:bg-[#FDF7F1] transition-all">
                <div className="w-9 h-9 rounded-lg bg-[#FDF2EF] text-[#D2673E] flex items-center justify-center shrink-0">
                  <GraduationCap size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#1F2937] truncate group-hover:text-[#D2673E]">{u.name}</div>
                  <div className="mt-0.5 text-[11px] text-[#4B5563] flex items-center gap-2">
                    <span className="uppercase tracking-wider">{(u.education_levels || []).join(" · ") || "—"}</span>
                    {reviewsOn && (u.avg_rating || 0) > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <StarRating value={u.avg_rating || 0} size={10} />
                        <span>{(u.avg_rating || 0).toFixed(1)}</span>
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-[10px] text-[#4B5563]">{formatSppRange(u.spp_min, u.spp_max)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-4 pt-4 border-t border-[#EAE7E0] text-xs text-[#4B5563] italic">Belum ada unit terdaftar di cabang ini.</div>
      )}
    </div>
  );
}
